import { ContentHeader } from "../components/ContentHeader";
import { IncidentBoard } from "./IncidentBoard";
import { useApi } from "../hooks/useApi";
import type { ActiveIncidents } from "../api/types";
import { OPERATIONS } from "../nav";

export function IncidentsTab() {
  const meta = OPERATIONS.find((e) => e.id === "incidents")!;
  const { data: r, loading, error } = useApi<ActiveIncidents>("/command-center/incidents/active");

  if (loading) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="loading"><div className="loading-spinner" /><p>Loading active incidents…</p></div>
      </>
    );
  }
  if (error || !r) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="empty">
          command-center-api not reachable. Run <code>./serve.sh</code> from <code>WaffyMonitoring/</code>.
        </div>
      </>
    );
  }

  const sev = r.by_severity || {};
  const tl = r.timeline || [];

  return (
    <>
      <ContentHeader title={meta.label} description={meta.desc} />
      <IncidentBoard />
      <div className="section-header"><h2>Live signals (24h)</h2></div>
      <div className="cov-summary">
        <span className={`cov-pill ${r.active_alerts > 0 ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{r.active_alerts}</strong> active alerts
        </span>
        {sev.critical ? (
          <span className="cov-pill crit"><span className="dot" /><strong>{sev.critical}</strong> critical</span>
        ) : null}
        {sev.warning ? (
          <span className="cov-pill warn"><span className="dot" /><strong>{sev.warning}</strong> warning</span>
        ) : null}
        <span className="cov-pill ok"><span className="dot" /><strong>{r.deploys_24h}</strong> deploys 24h</span>
      </div>

      {tl.length === 0
        ? <div className="timeline-empty">All clear — no active alerts and no deploys in the last 24 hours.</div>
        : (
          <div className="timeline">
            {tl.map((e, i) => {
              const cls = e.kind === "alert" ? (e.severity === "critical" ? "alert" : "alert-warn") : "deploy";
              const time = e.ts ? new Date(e.ts).toLocaleString("en-US", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }) : "";
              return (
                <div key={i} className={`timeline-event ${cls}`}>
                  <div className="timeline-time">{time} · <span className={`kind-tag ${e.kind}`}>{e.kind}</span></div>
                  <div className="timeline-title">
                    {e.title ?? "?"}{" "}
                    {e.service && <code style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-muted)" }}>{e.service}</code>}
                  </div>
                  {e.summary && <div className="timeline-meta">{e.summary}</div>}
                  {e.runbook && <div className="timeline-meta"><a href={e.runbook} target="_blank" rel="noopener">runbook ↗</a></div>}
                  {e.url && <div className="timeline-meta"><a href={e.url} target="_blank" rel="noopener">build ↗</a></div>}
                </div>
              );
            })}
          </div>
        )}

      <section style={{ marginTop: 24 }}>
        <div className="section-header"><h2>Postmortems</h2></div>
        <div className="empty" style={{ textAlign: "start", padding: "20px 24px" }}>
          Recent postmortems live in{" "}
          <a href="https://github.com/WaffyApp/WaffyMonitoring/tree/main/runbooks/postmortems" target="_blank" rel="noopener">
            <code>runbooks/postmortems/</code>
          </a>.
          Every SEV1/SEV2 needs a draft within 48h and finalized within 7d — the Command Center will track this automatically as incidents are declared via the
          {" "}<strong>Declare Incident</strong> button.
        </div>
      </section>
    </>
  );
}
